import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Building, Folder, Globe, Layers, Network, Server, Shield, Star } from 'lucide-react'
import Modal from '../Modal/Modal'

const ICON_OPTIONS = [
  { key: 'folder', Icon: Folder },
  { key: 'server', Icon: Server },
  { key: 'network', Icon: Network },
  { key: 'globe', Icon: Globe },
  { key: 'star', Icon: Star },
  { key: 'box', Icon: Box },
  { key: 'layers', Icon: Layers },
  { key: 'building', Icon: Building },
  { key: 'shield', Icon: Shield },
]

const COLOR_OPTIONS = [
  { key: 'default', bg: 'bg-white', border: 'border-gray-300' },
  { key: 'blue', bg: 'bg-blue-100', border: 'border-blue-400' },
  { key: 'green', bg: 'bg-green-100', border: 'border-green-400' },
  { key: 'yellow', bg: 'bg-yellow-100', border: 'border-yellow-400' },
  { key: 'red', bg: 'bg-red-100', border: 'border-red-400' },
  { key: 'purple', bg: 'bg-purple-100', border: 'border-purple-400' },
  { key: 'orange', bg: 'bg-orange-100', border: 'border-orange-400' },
  { key: 'teal', bg: 'bg-teal-100', border: 'border-teal-400' },
  { key: 'gray', bg: 'bg-gray-100', border: 'border-gray-400' },
]

export default function GroupModal({ isOpen, onClose, onSave, initialData, loading, simplified = false, parentGroupName = null }) {
  const { t } = useTranslation()
  const [name, setName] = useState(initialData?.name || '')
  const [description, setDescription] = useState(initialData?.description || '')
  const [iconKey, setIconKey] = useState(initialData?.iconKey || 'folder')
  const [color, setColor] = useState(initialData?.color || 'default')
  const [nameError, setNameError] = useState('')

  const reservedName = t('groups.breadcrumbRoot')

  useEffect(() => {
    if (isOpen) {
      setName(initialData?.name || '')
      setDescription(initialData?.description || '')
      setIconKey(initialData?.iconKey || 'folder')
      setColor(initialData?.color || 'default')
      setNameError('')
    }
  }, [isOpen, initialData])

  const isEdit = !!initialData?.id

  const handleSave = () => {
    if (!name.trim()) { setNameError(t('groups.modal.nameRequired')); return }
    if (name.trim().toLowerCase() === reservedName.toLowerCase()) {
      setNameError(t('groups.modal.reservedName', { name: reservedName }))
      return
    }
    setNameError('')
    onSave({ name: name.trim(), description: description.trim(), iconKey, color })
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t('groups.modal.titleEdit') : t('groups.modal.titleNew')}
    >
      <div className="space-y-4">
        {!isEdit && parentGroupName && (
          <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            <Folder className="w-4 h-4 flex-shrink-0" />
            <span>{t('groups.modal.willCreateInside')} <strong>{parentGroupName}</strong></span>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('groups.modal.nameLabel')}</label>
          <input
            type="text"
            className={`input w-full ${nameError ? 'border-red-400' : ''}`}
            value={name}
            onChange={(e) => { setName(e.target.value); setNameError('') }}
            placeholder={t('groups.modal.namePlaceholder')}
            autoFocus
          />
          {nameError && <p className="text-xs text-red-500 mt-1">{nameError}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('groups.modal.descriptionLabel')} <span className="text-gray-400 font-normal">{t('groups.modal.optional')}</span></label>
          <textarea
            className="input w-full resize-none"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('groups.modal.descriptionPlaceholder')}
          />
        </div>

        {!simplified && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('groups.modal.iconLabel')}</label>
              <div className="flex flex-wrap gap-2">
                {ICON_OPTIONS.map(({ key, Icon }) => (
                  <button
                    key={key}
                    type="button"
                    title={t(`groups.modal.icon.${key}`)}
                    onClick={() => setIconKey(key)}
                    className={[
                      'w-9 h-9 rounded-lg flex items-center justify-center border-2 transition-colors',
                      iconKey === key
                        ? 'border-blue-500 bg-blue-50 text-blue-600'
                        : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300',
                    ].join(' ')}
                  >
                    <Icon className="w-4 h-4" />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('groups.modal.colorLabel')}</label>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map(({ key, bg, border }) => (
                  <button
                    key={key}
                    type="button"
                    title={t(`groups.modal.color.${key}`)}
                    onClick={() => setColor(key)}
                    className={[
                      `w-7 h-7 rounded-full border-2 ${bg} transition-transform`,
                      color === key ? `ring-2 ring-offset-1 ring-blue-500 ${border} scale-110` : `${border} hover:scale-105`,
                    ].join(' ')}
                  />
                ))}
              </div>
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={loading}>
            {t('common.buttons.cancel')}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? t('groups.modal.saving') : isEdit ? t('common.buttons.save') : t('groups.modal.create')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
